import React, { useCallback, useEffect, useRef, useState } from "react";
import { DatePicker, Form, Input, Select, Space, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { CountryData } from "../../../staticData/countryData";
import { blockInvalidChar, convertToDateObject, disableFutureDates, getErrorFromItems, handleValidatePayload, isDateInRange, isEmpty, validateDob } from "../../../Utils/utils";
import {
  addressAnalyticsEvent,
  emailEnteredEvent,
  emailSelectedEvent,
  fullNameEnteredEvent,
  fullNameSelectedEvent,
  mobileNumberEnteredEvent,
  mobileNumberSelectedEvent,
  customFieldSelectedEvent,
  customFieldEnteredEvent,
} from "../../../Utils/uiAnalytics";
import { useFieldAnalytics } from "../../../Utils/useFieldAnalytics";
import { useDispatch, useSelector } from "react-redux";
import { countryPinCodeData } from "../../../staticData/countryPinCodeData";
import CustomButton from "../../resuableComponents/CustomButton";
import "./deliveryAddress.css";
import { selectIsMobileLayout } from "../../../slices/MobileLayoutSlice";
import {
  collpaseAddressComponent,
  getAllPaymentMethods,
  updateSavedAddressData,
  updateShopperDetails,
} from "../../../slices/PaymentSessionDetailsSlice";
import CollpaseAddress from "./collpaseAddress";
import { UserOutlined } from "@ant-design/icons";
import MobileAddressButton from "./mobileAddressButon";
import TranslateText from "../../resuableComponents/TranslateText";
import dayjs from "dayjs";
import CustomDatePicker from "../../resuableComponents/CustomDatePicker";

export default function BasicDetails({
  addressFieldValue,
  setShowAddAddressOption,
  setSelectedCardButton,
  setAddressFieldValue,
  handleGuestData,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const browserCountry = useSelector((state) => state.paymentSessionDetails.browserCountry);
  const addressCollapse = useSelector((state) => state.paymentSessionDetails.addressCollapse);
  const isShippingAddressDisplay = useSelector((state) => state.paymentSessionDetails.isShippingAddressDisplay);
  const { showEmailAddress, showFullName, showContactNumber, showPan, showDob } = useSelector((state) => state.additionalComponents)
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));
  const guestCheckout = useSelector((state) => state.shopperInfo.guestCheckout);
  const shopperinfo = useSelector((state) => state.shopperInfo.shopperinfo);
  const [form] = Form.useForm();
  const { trackFieldSelected, trackFieldEntered, trackAutofillEntered } = useFieldAnalytics();

  // Stable per-field event callbacks — trackFieldSelected/trackFieldEntered dedupe by
  // function identity, so each custom field needs its own fixed reference to carry
  // its fieldName through into eventAttrs.
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("");
  const [payloadData, setPayloadData] = useState("");
  const [disableButton, setDisableButton] = useState(true);
  const filterCountry = (input, option) => (option?.props?.children ?? "").toLowerCase().includes(input.toLowerCase());
  const handlePhoneDialCode = (phoneNumber) => {
    const normalizedPhoneNumber = phoneNumber?.startsWith("+") ? phoneNumber : !isEmpty(phoneNumber) ? `+${phoneNumber}` : '';
    const dialCode = CountryData?.find((country) => normalizedPhoneNumber?.startsWith(country.dial_code));
    const phoneNumberWithoutDialCode = dialCode ? normalizedPhoneNumber.replace(dialCode.dial_code, "") : normalizedPhoneNumber;
    const country = CountryData.find((country) => normalizedPhoneNumber.startsWith(country.dial_code));

    if (country) {
      return { dialCode: country.dial_code, phone: phoneNumberWithoutDialCode }
    } else {
      return { dialCode: browserCountry?.dial_code || "+91", phone: phoneNumberWithoutDialCode }
    }
  }

  useEffect(() => {
    if (!isEmpty(shopperinfo?.shopperRef)) {
      const { dialCode, phone } = handlePhoneDialCode(shopperinfo?.shopperRef)
      setPhoneNumber(phone);
      setPhoneDialCode(dialCode);
      form.setFieldsValue({ phoneNumber: phone });
    }
  }, [shopperinfo, form]);
  useEffect(() => {
    if (sessionDetails && sessionDetails?.paymentDetails?.shopper) {
      const { firstName, lastName, email, panNumber, dateOfBirth, phoneNumber } = sessionDetails.paymentDetails.shopper;
      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`;
        setFullName(fullName);
        form.setFieldsValue({ fullName: fullName });
      } else if (firstName) {
        setFullName(firstName);
        form.setFieldsValue({ fullName: firstName });
      } else if (lastName) {
        setFullName(lastName);
        form.setFieldsValue({ fullName: lastName });
      } else {
        setFullName("");
        form.setFieldsValue({ fullName: "" });
      }
      setEmailAddress(email);
      form.setFieldsValue({ email: email });

      setPanNumber(panNumber);
      form.setFieldsValue({ panNumber: panNumber });

      const { phone, dialCode } = handlePhoneDialCode(phoneNumber)
      setPhoneNumber(phone);
      setPhoneDialCode(dialCode);
      form.setFieldsValue({ phoneNumber: phone });

      if (!isEmpty(dateOfBirth) && isDateInRange(dateOfBirth)) {
        const date = convertToDateObject(dateOfBirth);
        setDateOfBirth(dateOfBirth);
        form.setFieldsValue({ dateOfBirth: date });
      }
    }
  }, [sessionDetails, shopperinfo, form]);
  useEffect(() => {
    if (guestCheckout) {
      if (sessionDetails) {
        const { phone, dialCode } = handlePhoneDialCode(sessionDetails?.paymentDetails?.shopper?.phoneNumber)
        setPhoneNumber(phone);
        form.setFieldsValue({ phoneNumber: phone });
      }
    }
  }, [guestCheckout, sessionDetails, form]);
  useEffect(() => {
    if (guestCheckout) {
      const { phone, dialCode } = handlePhoneDialCode(sessionDetails?.paymentDetails?.shopper?.phoneNumber);
      setPhoneDialCode(dialCode);
    }
  }, [guestCheckout, sessionDetails]);
  const validateEmail = (rule, value, callback) => {
    // const regex = /^[^\s@']+@[^\s@']+\.[^\s@']+$/;
    const regex = /^(?=.{1,64})(?=^.+@)(?!.*\.\.)(?=^[^\s@]+@)[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;
    if (!value) {
      callback(t("REQUIRED"));
    } else if (!regex.test(value)) {
      callback(t("INVALID_EMAIL"));
      setDisableButton(true);
    } else {
      callback();
    }
  };
  const validatePan = (rule, value, callback) => {
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!value) {
      callback(t("REQUIRED"));
    } else if (!regex.test(value)) {
      callback(t("INVALID_PAN"));
      setDisableButton(true);
    } else {
      callback();
    }
  }
  const handleFullName = (e) => {
    setFullName(e.target.value);
  };
  const validatePhoneNumber = (rule, value, callback) => {
    const telNo = phoneNumber;
    const re = /^[0-9\b]+$/;
    const country = countryPinCodeData.find((country) => country.dial_code === phoneDialCode);
    const expectedLengths = country.phoneNumberLength.map((length) => length);
    if (telNo === "") {
      callback(t("REQUIRED"));
    } else if (country) {
      if (!expectedLengths.includes(telNo.length)) {
        callback(`${t("MOBILE_NUMBER_MUST_BE")} ${expectedLengths} ${t("DIGITS")}`);
      } else if (telNo.charAt(0) === "0") {
        callback(t("INVALID_NUMBER"));
      }
    } else if (re.test(telNo)) {
      callback(t("INVALID_NUMBER"));
    }
    callback();
  };
  const handlePhoneChange = (e) => {
    const telNo = e?.target?.value;
    const re = /^[0-9\b]+$/;
    if (telNo === "" || re.test(telNo)) {
      setPhoneNumber(telNo);
    }
  };
  useEffect(() => {
    const handleLanguageChange = () => {
      let e;
      e = { target: { value: phoneNumber } };
      handlePhoneChange(e)
    }
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, phoneNumber])
  const handleEmailAddress = (e) => {
    setEmailAddress(e.target.value);
  };
  const handleCountryChange = (value) => {
    setPhoneDialCode(value);
    form.validateFields(["phoneNumber"]);
  };
  const handlePan = (e) => {
    let pan = e.target.value.trim().toUpperCase();
    setPanNumber(pan);
    form.setFieldsValue({ panNumber: pan });
    form.validateFields(["panNumber"]);
  };
  const handleDob = (date, dateString) => {
    if (!date) {
      setDateOfBirth(null); // When no date is selected, set it to null
    } else {
      const formattedDate = dayjs(date).format('DD-MM-YYYYTHH:mm:ss[Z]');
      setDateOfBirth(formattedDate);
    }
  }

  useEffect(() => {
    const hasError = (showFullName ? !fullName : false)
      || (showContactNumber ? !phoneNumber : false)
      || (showEmailAddress ? !emailAddress : false)
      || (showPan ? !panNumber : false)
      || (showDob ? !dateOfBirth : false);
    const hasFormErrors = () => {
      const errors = form.getFieldsError();
      return errors.some(field => field.errors.length > 0);
    };
    const hasValidationError = hasFormErrors()
    if (hasError || phoneError?.length || hasValidationError) setDisableButton(true);
    else setDisableButton(false);

    if (!showFullName && !showContactNumber && !showEmailAddress && !showPan && !showDob && !isShippingAddressDisplay) {
      dispatch(collpaseAddressComponent());
    }
  }, [fullName, phoneNumber, emailAddress, showFullName, showContactNumber, showEmailAddress, phoneError, showDob, dateOfBirth, showPan, panNumber]);

  const handleBasicDetails = () => {
    const selectedCardData = {
      name: fullName,
      phoneNumber: isEmpty(phoneNumber) ? '' : phoneDialCode + phoneNumber,
      email: emailAddress,
      panNumber,
      dateOfBirth,
    };
    dispatch(updateSavedAddressData(selectedCardData));
    dispatch(updateShopperDetails({ selectedCardData }));
    dispatch(collpaseAddressComponent());
    // dispatch(getAllPaymentMethods({
    //   token: sessionDetails?.token,
    //   countryCode: browserCountry?.code || ""
    // }));
  };
  useEffect(() => {
    if (sessionDetails?.paymentDetails?.shopper) {
      const { firstName = "", lastName = "", phoneNumber, email, panNumber, dateOfBirth } = sessionDetails.paymentDetails.shopper;
      const noBasicFields = !showFullName && !showEmailAddress && !showContactNumber && !isShippingAddressDisplay;

      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      if ((!showFullName || (showFullName && fullName)) &&
        (!showEmailAddress || (showEmailAddress && email)) &&
        (!showContactNumber || (showContactNumber && phoneNumber)) &&
        (noBasicFields || !showDob || (showDob && dateOfBirth)) &&
        (noBasicFields || !showPan || (showPan && panNumber))) {

        const name = [firstName, lastName].filter(Boolean).join(" ");
        const selectedCardData = {
          name,
          phoneNumber: phoneNumber,
          email: email,
          panNumber,
          dateOfBirth,
        };
        dispatch(updateSavedAddressData(selectedCardData));
        dispatch(updateShopperDetails({ selectedCardData }));
        dispatch(collpaseAddressComponent());
      }
    }
  }, [sessionDetails, showEmailAddress]);

  useEffect(() => {
    if (Object.keys(addressFieldValue)?.length) {
      setFullName(addressFieldValue.name);
      const { phone, dialCode } = handlePhoneDialCode(addressFieldValue?.phoneNumber);
      setPhoneNumber(phone);
      setPhoneDialCode(dialCode);

      setEmailAddress(addressFieldValue?.email);
      setPanNumber(addressFieldValue?.panNumber);

      if (!isEmpty(addressFieldValue?.dateOfBirth) && isDateInRange(addressFieldValue?.dateOfBirth)) {
        const date = convertToDateObject(addressFieldValue?.dateOfBirth);
        setDateOfBirth(addressFieldValue?.dateOfBirth);
        form.setFieldsValue({ dateOfBirth: date });
      }

      form.setFieldsValue({
        fullName: addressFieldValue?.name,
        phoneNumber: phone,
        email: addressFieldValue?.email,
        panNumber: addressFieldValue?.panNumber
      });
    }
  }, [addressFieldValue, form]);
  useEffect(() => {
    const formData = {
      name: fullName,
      email: emailAddress,
      phoneNumber: phoneNumber,
      panNumber: panNumber,
      dateOfBirth: dateOfBirth,
      phoneDialCode: phoneDialCode,
    };
    setPayloadData(formData);
  }, [fullName, emailAddress, phoneDialCode, phoneNumber, dateOfBirth, panNumber]);

  useEffect(() => {
    const handleLanguageChange = () => {
      const fieldsWithErrors = ['fullName', 'phoneNumber', 'email', 'panNumber', 'dateOfBirth'].filter((field) =>
        form.getFieldError(field).length > 0
      );

      if (fieldsWithErrors.length > 0) {
        form.validateFields(fieldsWithErrors);
      }
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, form]);

  const onFinish = async (values) => {
    try {
      if (showContactNumber) {
        const payload = {
          phoneNumber: isEmpty(phoneNumber) ? '' : phoneDialCode + phoneNumber,
          uniqueReference: phoneNumber,
        };
        addressAnalyticsEvent();
        const data = await handleValidatePayload(payload);
        if (data?.length) {
          const phoneNumberError = getErrorFromItems(data, 'phoneNumber');
          form.setFields([
            {
              name: 'phoneNumber',
              errors: [phoneNumberError],
            },
          ]);
          return;
        }
      }
      handleBasicDetails();
    } catch (err) {
      console.log('err', err);
    }
  };
  const onFinishFailed = errorInfo => {
    // console.log('Failed:', errorInfo);
  };

  const DobComponent = useCallback(() => {
    return (
      <div className="addressBox">
        <Form.Item
          label={
            <>
              <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                <TranslateText label={"DATE_OF_BIRTH_INCORPORATION"}>Date of Birth/Incorporation</TranslateText>*
              </Typography>
              <Tooltip
                placement="right"
                title={t("DOB_DESCRIPTION_INFO")}
              >
                <img
                  src="/assets/address/help-circle.svg"
                  alt="help-circle"
                  style={{ marginLeft: "4px", width: "16px" }}
                />
              </Tooltip>
            </>
          }
          name="dateOfBirth"
          rules={[
            {
              required: true,
              message: t("REQUIRED")
            },
            {
              validator: validateDob,
            },
          ]}
        >
          <CustomDatePicker
            value={dateOfBirth}
            onChange={handleDob}
            disabledDate={disableFutureDates}
            t={t}
          />
          {/* <DatePicker className={"date-picker-w-100"}
            format={'MM-DD-YYYY'}
            value={dateOfBirth ? dayjs(dateOfBirth) : null}
            onChange={handleDob}
            disabledDate={disableFutureDates}
            renderExtraFooter={() => null}
            placeholder={t("MM_DD_YYYY")}
            showToday={false}
            allowClear={false}
            inputReadOnly
          /> */}
        </Form.Item>
      </div>
    )
  }, []);

  return (
    <>
      {
        (showFullName || showContactNumber || showEmailAddress || showPan || showDob) && (
          <>
            {addressCollapse ? (
              <>
                <div className="deliveryForm">
                  <div className="deliveryAddressHeader">
                    <div className="headerDiv">
                      <UserOutlined />
                      <Typography className="deeliveryHeading dynamic-font-family">
                        <TranslateText label={"PERSONAL_DETAILS"}>Personal Details</TranslateText>
                      </Typography>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "0px 0px 12px 12px",
                      border: "1px solid #f0f0f0",
                      borderTop: "none",
                    }}
                  >
                    <CollpaseAddress
                      setShowAddAddressOption={setShowAddAddressOption}
                      setSelectedCardButton={setSelectedCardButton}
                      setAddressFieldValue={setAddressFieldValue}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="deliveryForm">
                <div className="deliveryAddressHeader">
                  <div className="headerDiv">
                    <UserOutlined />
                    <Typography className="deeliveryHeading dynamic-font-family">
                      <TranslateText label={"PERSONAL_DETAILS"}>Personal Details</TranslateText>
                    </Typography>
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "white",
                    paddingTop: "8px",
                    borderRadius: "0px 0px 12px 12px",
                    border: "1px solid #f0f0f0",
                    borderTop: "none",
                  }}
                >
                  <Form
                    requiredMark={false}
                    layout="vertical"
                    name="addresss"
                    form={form}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                  >
                    <div className="cardPadding">
                      <div className="addressContainer">
                        {showFullName && (
                          <div className="addressBox">
                            <Form.Item
                              label={
                                <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                                  <TranslateText label={"FULL_NAME"}>Full Name</TranslateText>*
                                </Typography>
                              }
                              name="fullName"
                              rules={[
                                {
                                  required: true,
                                  message: t("REQUIRED"),
                                },
                              ]}
                            >
                              <>
                                <Input
                                  placeholder={t("FULL_NAME")}
                                  value={fullName}
                                  onChange={handleFullName}
                                  onFocus={() => trackFieldSelected(fullNameSelectedEvent)}
                                  onInput={(e) => trackAutofillEntered(e, fullNameEnteredEvent)}
                                  onBlur={() => trackFieldEntered(fullNameEnteredEvent, fullName)}
                                />
                              </>
                            </Form.Item>
                          </div>
                        )}

                        {showContactNumber && (
                          <div className="addressBox">
                            <Form.Item
                              label={
                                <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                                  <TranslateText label={"MOBILE_NUMBER"}>Mobile Number</TranslateText>*
                                </Typography>
                              }
                              name="phoneNumber"
                              rules={[
                                {
                                  required: true,
                                  message: t("REQUIRED")
                                },
                              ]}
                            >
                              <Space
                                direction="vertical"
                                size="middle"
                                style={{ width: "100%" }}
                              >
                                <Space.Compact>
                                  <Select
                                    className="contactNumber"
                                    showSearch
                                    filterOption={filterCountry}
                                    value={phoneDialCode}
                                    onChange={handleCountryChange}
                                  >
                                    {CountryData.map((country) => (
                                      <Select.Option
                                        key={country.code}
                                        value={country.dial_code}
                                      >
                                        {country.dial_code}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                  <Input
                                    type="number"
                                    value={phoneNumber}
                                    style={{ width: "100%" }}
                                    onKeyDown={blockInvalidChar}
                                    onChange={handlePhoneChange}
                                    onFocus={() => trackFieldSelected(mobileNumberSelectedEvent)}
                                    onBlur={() => trackFieldEntered(mobileNumberEnteredEvent, phoneNumber)}
                                    placeholder={t("MOBILE_NUMBER")}
                                    pattern="[0-9]*"
                                    inputMode="tel"
                                    onInput={(e) => {
                                      e.target.value = e.target.value.replace(/^0+/, '');
                                      trackAutofillEntered(e, mobileNumberEnteredEvent);
                                    }}
                                  />
                                </Space.Compact>
                              </Space>
                            </Form.Item>
                          </div>
                        )}

                        {showEmailAddress && (
                          <div className="addressBox">
                            <Form.Item
                              label={
                                <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                                  <TranslateText label={"EMAIL"}>Email</TranslateText>*
                                </Typography>
                              }
                              name="email"
                              rules={[
                                {
                                  required: true,
                                  validator: validateEmail,
                                },
                              ]}
                            >
                              <>
                                <Input
                                  placeholder={t("EMAIL_ADDRESS")}
                                  value={emailAddress}
                                  onChange={handleEmailAddress}
                                  onFocus={() => trackFieldSelected(emailSelectedEvent)}
                                  onInput={(e) => trackAutofillEntered(e, emailEnteredEvent)}
                                  onBlur={() => trackFieldEntered(emailEnteredEvent, emailAddress)}
                                />
                              </>
                            </Form.Item>
                          </div>
                        )}

                        {showPan && (
                          <div className="addressBox">
                            <Form.Item
                              label={
                                <>
                                  <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                                    <TranslateText label={"PAN"}>Pan</TranslateText>*
                                  </Typography>
                                  <Tooltip
                                    placement="right"
                                    title={t("PAN_DESCRIPTION_INFO")}
                                  >
                                    <img
                                      src="/assets/address/help-circle.svg"
                                      alt="help-circle"
                                      style={{ marginLeft: "4px", width: "16px" }}
                                    />
                                  </Tooltip>
                                </>
                              }
                              name="panNumber"
                              rules={[
                                {
                                  required: true,
                                  validator: validatePan,
                                },
                              ]}
                            >
                              <>
                                <Input
                                  placeholder={t("PAN")}
                                  value={panNumber}
                                  onChange={handlePan}
                                />
                              </>
                            </Form.Item>
                          </div>
                        )}
                        {
                          !showPan && showDob && (
                            <DobComponent />
                          )
                        }
                        {
                          showDob && showPan && (
                            <DobComponent />
                          )
                        }
                      </div>
                    </div>
                    <div
                      className={isMobileLayout ? "" : "continueButtonDiv"}
                      style={{ display: isMobileLayout ? "none" : "" }}
                    >
                      <CustomButton
                        text={
                          <div className={`dynamic-font-family ${disableButton ? '' : 'dynamic-button-text'}`}
                            style={{ color: disableButton ? 'inherit !important' : sessionDetails?.merchantDetails?.checkoutTheme?.buttonTextColor }}
                          >
                            <TranslateText label={"CONTINUE"}>Continue </TranslateText>
                          </div>
                        }
                        buttonType={"primary-btn"}
                        padding={"10px 50px"}
                        // onClick={() => handleBasicDetails()}
                        htmlType="submit"
                      // disabled={disableButton}
                      />
                    </div>
                    <div style={{ display: isMobileLayout ? "" : "none" }}>
                      <MobileAddressButton
                        disabled={disableButton}
                        showCancelButton={false}
                        setShowAddAddressOption={setShowAddAddressOption}
                        setSelectedAddressItem={""}
                        selectedAddressItem={""}
                        payloadData={payloadData}
                        otherLabel={""}
                        selectedSavedAddress={""}
                        guestCheckout={""}
                        handleGuestData={handleGuestData}
                        handleBasicDetails={handleBasicDetails}
                      />
                    </div>
                  </Form>
                </div>
              </div>
            )}
          </>
        )
      }

    </>
  );
}
